export type FirebaseAuthErrorContext = 'login' | 'passwordReset';

const SHARED_MESSAGES: Record<string, string> = {
  'auth/invalid-login-credentials': 'Incorrect email or password. Please try again.',
  'auth/invalid-credential': 'Incorrect email or password. Please try again.',
  'auth/wrong-password': 'Incorrect email or password. Please try again.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/network-request-failed': 'Network error. Check your connection and try again.',
};

const CONTEXT_MESSAGES: Partial<
  Record<string, Partial<Record<FirebaseAuthErrorContext, string>>>
> = {
  'auth/user-not-found': {
    login: 'Incorrect email or password. Please try again.',
    passwordReset: 'No account found with this email address.',
  },
};

const FALLBACK_MESSAGES: Record<FirebaseAuthErrorContext, string> = {
  login: 'Unable to sign in. Please try again.',
  passwordReset: 'Unable to send password reset email. Please try again.',
};

function getErrorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

export function getFirebaseAuthErrorMessage(
  error: unknown,
  context: FirebaseAuthErrorContext = 'login'
): string {
  const code = getErrorCode(error);
  if (code) {
    const contextMessage = CONTEXT_MESSAGES[code]?.[context];
    if (contextMessage) return contextMessage;

    const sharedMessage = SHARED_MESSAGES[code];
    if (sharedMessage) return sharedMessage;
  }

  if (error instanceof Error && error.message.startsWith('Firebase:')) {
    return FALLBACK_MESSAGES[context];
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return FALLBACK_MESSAGES[context];
}
